import React, {
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type KeyboardEvent,
} from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import styles from './ConversationPanel.module.css';
import promptStyles from '../PromptPanel/PromptPanel.module.css';
import MessageContent from './MessageContent';
import MessageAttachment from './MessageAttachment';
import UserMessageScope from './UserMessageScope';
import type AssistantMessage from '../../../framework/models/AssistantMessage';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import PromptEditor, { type EditorApi } from '../PromptPanel/PromptEditor';
import classNames from 'classnames';
import { FROZEN_CHAT_ERROR } from '../../constants';

interface UserMesssageProps {
  message: AssistantMessage;
}

const UserMessage: React.FC<UserMesssageProps> = ({
  message,
}: UserMesssageProps) => {
  const messageRef = useRef<HTMLDivElement>(null);
  const controller = useController<AssistantViewController>();
  const { model, readOnly, size } = controller.state;
  const [editing, setEditing] = useState(false);
  const [prompt, setPrompt] = useState(message.content);
  const editorApiRef = React.useRef<EditorApi>(null);

  useLayoutEffect(() => {
    // Measure the position of the message so we can use it for scroll positioning.
    if (message === model.lastMessage('user') && messageRef.current) {
      const element = messageRef.current;
      controller.actionSetLayoutData('topOfMessage', element.offsetTop);
      controller.actionSetLayoutData(
        'bottomOfMessage',
        element.offsetTop + element.offsetHeight
      );
    }
  });

  let attachments: ReactElement | null = null;
  if (message.attachments && message.attachments.length > 0) {
    const imageAttachments = message.attachments.filter(a =>
      a.contentType?.startsWith('image/')
    );
    attachments = (
      <div
        className={classNames(
          promptStyles.attachments,
          styles.messageAttachments
        )}
      >
        {imageAttachments.map((attachment, index) => (
          <MessageAttachment
            key={attachment.uniqueId}
            attachment={attachment}
          />
        ))}
      </div>
    );
  }

  if (editing) {
    const handleSubmit = () => {
      setEditing(false);
      // @ts-expect-error Local update that won't be sent to the server.
      message.content = prompt;
      model.retryMessage(message);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit();
      }
    };

    return (
      <div className={styles.userMessage}>
        {attachments}
        <UserMessageScope message={message} />
        <div className={classNames(styles.userMessageContent, styles.editing)}>
          <div
            className={promptStyles.promptBubble}
            onKeyDown={e => handleKeyDown(e)}
          >
            <div className={promptStyles.promptScoller}>
              <PromptEditor
                initialValue={message.content.trim()}
                setPrompt={prompt => setPrompt(prompt)}
                disabled={controller.frozenOrNoUsageAvailable()}
                editorApi={editorApiRef}
                size={size}
              />
              {model.isChatFrozen && (
                <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
              )}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--spacer-2)',
            }}
          >
            <span className={styles.userEditHelp}>
              <i className='fa-regular fa-pencil' />
              Restart the chat from here
            </span>
            <span style={{ display: 'flex', gap: 'var(--spacer-2)' }}>
              <aha-button
                kind='gray'
                size='small'
                onClick={() => {
                  setEditing(false);
                }}
              >
                Cancel
              </aha-button>
              <aha-button
                kind='primary'
                size='small'
                onClick={() => {
                  handleSubmit();
                }}
              >
                Save
              </aha-button>
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={messageRef}
      className={styles.userMessage}
      data-message-id={message.id}
    >
      {attachments}
      <UserMessageScope message={message} />
      <div className={styles.userMessageContent}>
        <MessageContent body={message.content} contentType='text/markdown' />
      </div>

      <div className={styles.userMessageButtons}>
        {!readOnly && (
          <>
            {message.id && (
              <>
                <aha-button
                  kind='icon'
                  size='mini'
                  {...(controller.frozenOrNoUsageAvailable()
                    ? { disabled: true }
                    : {})}
                  onClick={() =>
                    void controller.actionCreatePrompt(message.content)
                  }
                >
                  <i className='fa-regular fa-star' />
                </aha-button>
                <aha-tooltip>
                  {model.isChatFrozen ? FROZEN_CHAT_ERROR : 'Favorite'}
                </aha-tooltip>
                <aha-button
                  kind='icon'
                  size='mini'
                  {...(controller.frozenOrNoUsageAvailable()
                    ? { disabled: true }
                    : {})}
                  onClick={() => setEditing(true)}
                >
                  <i className='fa-regular fa-pencil' />
                </aha-button>
                <aha-tooltip>
                  {model.isChatFrozen ? FROZEN_CHAT_ERROR : 'Edit'}
                </aha-tooltip>
              </>
            )}
            {!model.active && message.id && (
              <>
                <aha-button
                  kind='icon'
                  size='mini'
                  onClick={() => model.retryMessage(message)}
                  {...(controller.frozenOrNoUsageAvailable()
                    ? { disabled: true }
                    : {})}
                >
                  <i className='fa-regular fa-arrows-rotate' />
                </aha-button>
                <aha-tooltip>
                  {model.isChatFrozen ? FROZEN_CHAT_ERROR : 'Retry from here'}
                </aha-tooltip>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ApplicationView(UserMessage);
